import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Clock, Check, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { ServiceCard } from "@/components/ServiceCard";
import { BRAND, getCategory, getService, servicesByCategory, waLink } from "@/data/site";

export default function ServiceDetail() {
  const { categorySlug, serviceSlug } = useParams();
  const service = getService(categorySlug, serviceSlug);
  const category = getCategory(categorySlug);
  if (!service || !category) return <Navigate to="/services" replace />;

  const related = servicesByCategory(categorySlug).filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <>
      <Seo
        title={`${service.name} in Manikonda, Hyderabad | Ody Luxurious Spa`}
        description={`${service.excerpt} Book at Ody Luxurious Spa, Manikonda — call or WhatsApp +91 90147 29756 for pricing & slots.`}
      />
      <PageHero
        eyebrow={category.name}
        title={service.name}
        subtitle={service.tagline}
        crumbs={[
          { label: "Services", to: "/services" },
          { label: category.name, to: `/services/${category.slug}` },
          { label: service.name },
        ]}
      />
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-3xl overflow-hidden bg-alabaster"
            >
              <img
                src={service.image}
                alt={`${service.name} at Ody Luxurious Spa`}
                decoding="async"
                className="w-full h-[340px] sm:h-[460px] object-cover"
              />
            </motion.div>
            <Reveal delay={0.1}>
              <div className="mt-10">
                <Eyebrow>What It Is</Eyebrow>
              </div>
            </Reveal>
            <div className="mt-5 space-y-6">
              {service.description.map((p, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <p className="text-base leading-relaxed text-stone2">{p}</p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <div className="mt-12">
                <Eyebrow>Benefits</Eyebrow>
                <ul className="mt-6 grid sm:grid-cols-2 gap-4">
                  {service.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="mt-0.5 rounded-full bg-sage/10 p-1.5 text-sage">
                        <Check size={13} />
                      </span>
                      <span className="text-sm text-ink/85">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <aside className="lg:col-span-2">
            <Reveal delay={0.2}>
              <div className="lg:sticky lg:top-28 rounded-3xl border border-bronze/15 bg-cream p-8">
                <p className="text-xs uppercase tracking-[0.3em] text-bronze">Reserve this service</p>
                <h3 className="mt-4 font-serif text-2xl text-ink">{service.name}</h3>
                {service.duration && (
                  <div className="mt-5 flex items-center gap-2.5 text-sm text-stone2">
                    <Clock size={15} className="text-gold" />
                    {service.duration} sessions
                  </div>
                )}
                <div className="gold-rule my-7" />
                <p className="text-xs uppercase tracking-[0.3em] text-bronze mb-3">Who It Is For</p>
                <p className="text-sm leading-relaxed text-stone2">{service.ideal}</p>
                <div className="gold-rule my-7" />
                <p className="text-sm text-stone2">
                  Pricing is personal — share a quick message and we will send the current rate
                  card and available slots.
                </p>
                <div className="mt-7 space-y-3">
                  <a
                    href={waLink(service.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="service-whatsapp-btn"
                    className="flex items-center justify-center gap-2.5 rounded-full bg-wa text-white px-6 py-4 text-xs uppercase tracking-[0.18em] transition-colors duration-300 hover:bg-wa-dark"
                  >
                    <MessageCircle size={15} /> WhatsApp for Pricing
                  </a>
                  <a
                    href={BRAND.tel}
                    data-testid="service-call-btn"
                    className="flex items-center justify-center gap-2.5 rounded-full border border-bronze/30 px-6 py-4 text-xs uppercase tracking-[0.18em] text-bronze transition-colors duration-300 hover:bg-bronze hover:text-cream"
                  >
                    <Phone size={15} /> Call {BRAND.phoneDisplay}
                  </a>
                </div>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
      {related.length > 0 && (
        <section className="py-20 bg-alabaster">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <Reveal>
                <h2 className="font-serif font-normal tracking-tight text-ink text-2xl sm:text-3xl">
                  More from {category.name}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <Link
                  to={`/services/${category.slug}`}
                  data-testid="related-category-link"
                  className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-bronze"
                >
                  View all
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((s, i) => (
                <ServiceCard key={s.slug} service={s} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
